import { MaplibreMap } from '../maplibre/MaplibreMap.js';

/**
 * Usage :
 *
 *  const container = MapPrinter.getPrintContainer();
 *  const map = MapFactory.getMap(new MapOptions({ elementId: container.id, width, height, extent }));
 *  map.addLayer(..your layer optionshere..), geojson;
 *  return MapPrinter.print(map, { width, height, container });
 *
 *
 */
export class MapPrinter {
  public static getPrintContainer(opts: { width: number; height: number }): HTMLDivElement {
    const container = document.createElement('div');
    container.style.width = `${opts.width}px`;
    container.style.height = `${opts.height}px`;
    document.body.appendChild(container);
    return container;
  }

  public static async loadHtml2Canvas(): Promise<any> {
    if (typeof window === 'undefined') return null; // Prevent running in Node.js
    const module = await import('html2canvas');
    return module.default ?? module;
  }

  public static async print(
    map: MaplibreMap,
    opts: { width: number; height: number; container: HTMLDivElement; legendHTML?: string },
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      map.addEventListener('loaded', () => { }); // Attendre que la carte soit chargée
      setTimeout(() => {
        map
          .getMapBoxMap()
          .getCanvas()
          .toBlob(async (blob: Blob | null) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const img = new Image();
              img.onload = async () => {
                const canvas = document.createElement('canvas');
                canvas.width = opts.width;
                canvas.height = opts.height;
                const context = canvas.getContext('2d');
                if (context) {
                  // Dessiner l'image de la carte sur le canvas
                  context.drawImage(img, 0, 0, opts.width, opts.height);

                  const printFinalize = () => {
                    // Convertir le canvas en Data URL
                    const dataUrl = canvas.toDataURL('image/png');
                    document.body.removeChild(opts.container);
                    resolve(dataUrl);
                  };

                  if (opts.legendHTML && opts.legendHTML.length > 0) {
                    try {
                      const { legendImg, legendWidth, legendHeight } = await MapPrinter.getLegendImage(opts.legendHTML);
                      const padding = 10;

                      context.drawImage(
                        legendImg,
                        padding, // x
                        opts.height - legendHeight - padding, // y
                        legendWidth, // largeur
                        legendHeight, // hauteur
                      );

                      printFinalize();
                    } catch (error) {
                      console.warn('Legend could not be generated, continuing without it:', error);
                      // Ignorer l'erreur et finaliser sans la légende
                      printFinalize();
                    }
                  } else {
                    // Si pas de légende, finaliser l'impression directement
                    printFinalize();
                  }
                }
              };
              img.onerror = () => {
                document.body.removeChild(opts.container);
                reject(new Error('Failed to load map canvas image'));
              };
              img.src = url;
            }
          });
      }, 2000);
    });
  }

  public static async getLegendImage(legendHTML: string) {
    return new Promise<any>(async (resolve, reject) => {
      const legendElement = document.createElement('div');
      legendElement.innerHTML = legendHTML;

      legendElement.style.position = 'absolute';
      legendElement.style.top = '0';
      legendElement.style.left = '0';
      legendElement.style.zIndex = '1000';
      legendElement.style.backgroundColor = 'white'; // Fond blanc pour la légende
      legendElement.style.padding = '5px'; // Ajouter un padding pour le contenu
      legendElement.style.boxSizing = 'border-box'; // Inclure le padding dans les dimensions
      legendElement.style.display = 'inline-block'; // Permettre à la largeur de s'adapter au contenu

      // Convertir le bloc HTML en image
      const html2canvas = await MapPrinter.loadHtml2Canvas();
      if (!html2canvas) throw new Error('html2canvas not loaded');
      document.body.appendChild(legendElement); // Ajouter l'élément au DOM

      // Récupérer les dimensions réelles de legendElement
      const legendRect = legendElement.getBoundingClientRect();
      const legendWidth = legendRect.width; // Largeur réelle
      const legendHeight = legendRect.height; // Hauteur réelle
      legendElement.style.width = `${legendWidth}px`; // Appliquer la largeur réelle
      legendElement.style.height = `${legendHeight}px`; // Appliquer la hauteur réelle

      console.log('Legend dimensions:', { width: legendWidth, height: legendHeight });

      let legendCanvas: HTMLCanvasElement;
      try {
        legendCanvas = await html2canvas(legendElement, {
          backgroundColor: '#ffffff',
          scale: 2, // Améliore la qualité de l'image
          logging: false,
          useCORS: true, // Permet de charger les images cross-origin
        });
      } catch (error) {
        document.body.removeChild(legendElement);
        console.error('html2canvas error:', error);
        reject(new Error(`Failed to convert legend to PNG: ${error}`));
        return;
      }
      document.body.removeChild(legendElement); // Supprimer l'élément après la conversion

      const legendImage = legendCanvas.toDataURL('image/png');

      if (!legendImage || !legendImage.startsWith('data:image')) {
        reject(new Error('Invalid legend image data URL generated'));
        return;
      }

      const legendImg = new Image();

      legendImg.onload = () => {
        resolve({ legendImg, legendWidth, legendHeight });
      };
      legendImg.onerror = (error) => {
        console.error('Legend image load error:', error);
        reject(new Error('Failed to load legend image from data URL'));
      };
      legendImg.src = legendImage;
    });
  }


}
